import React, { useState, useEffect, useRef, useCallback } from 'react';
import API from 'shared/api';
import {
  FiCpu,
  FiSend,
  FiX,
  FiCheck,
  FiAlertCircle,
  FiMessageSquare,
  FiChevronDown,
  FiClock,
  FiTrash2,
  FiPlus,
  FiMaximize2,
  FiVolume2,
  FiSquare,
} from 'react-icons/fi';
import { COL } from 'shared/darkColors';

function fmtMd(t) {
  if (!t) return '';
  const esc = t
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  // Listas (bullet y numeradas) antes que los saltos de linea.
  const lines = esc.split(/\n/);
  let inUl = false;
  let inOl = false;
  const out = [];
  for (const line of lines) {
    const ulMatch = line.match(/^\s*[-*]\s+(.*)$/);
    const olMatch = line.match(/^\s*(\d+)[.)]\s+(.*)$/);
    if (ulMatch) {
      if (!inUl) { out.push('<ul style="margin:4px 0 4px 18px;padding:0">'); inUl = true; }
      out.push(`<li>${ulMatch[1]}</li>`);
      continue;
    }
    if (olMatch) {
      if (!inOl) { out.push('<ol style="margin:4px 0 4px 18px;padding:0">'); inOl = true; }
      out.push(`<li>${olMatch[2]}</li>`);
      continue;
    }
    if (inUl) { out.push('</ul>'); inUl = false; }
    if (inOl) { out.push('</ol>'); inOl = false; }
    const h = line.match(/^(#{1,4})\s+(.*)$/);
    if (h) {
      const lvl = h[1].length;
      out.push(`<div style="font-weight:700;margin-top:6px;font-size:${15 - lvl}px">${h[2]}</div>`);
    } else {
      out.push(line);
    }
  }
  if (inUl) out.push('</ul>');
  if (inOl) out.push('</ol>');
  return out
    .join('\n')
    .replace(/\n/g, '<br/>')
    .replace(/\*\*(.+?)\*\*/gs, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/gs, '<em>$1</em>')
    .replace(
      /`(.+?)`/g,
      '<code style="background:#f1f5f9;padding:1px 4px;border-radius:3px;font-size:12px">$1</code>'
    );
}

const SUGERENCIAS = {
  chats: [
    'Cuantos pedidos hay pendientes hoy?',
    'Resume la actividad de los últimos 30 días',
    'Qué productos están agotados?',
    'Dame un resumen de ventas de esta semana',
  ],
  inventario: [
    'Cuantos productos tengo en total?',
    'Cuáles están agotados?',
    'Cuales son los mas vendidos este mes?',
    'Busca el producto con código...',
  ],
  ventas: [
    'Resumen de ventas de hoy',
    'Ventas de esta semana por dia',
    'Productos mas vendidos este mes',
    'Dashborad general de la empresa',
  ],
  pedidos: [
    'Cuantos pedidos pendientes hay?',
    'Pedidos en proceso de armado',
    'Busca el pedido #...',
    'Resumen de pedidos ultimos 7 dias',
  ],
  usuarios: [
    'Cuantos usuarios tiene mi empresa?',
    'Crea un nuevo asesor',
    'Cambia el rol de un usuario',
    'Lista los usuarios por sede',
  ],
  sedes: [
    'Cuantas sedes tengo activas?',
    'Cambia la dirección de una sede',
    'Dashboard general de la empresa',
  ],
  entregas: [
    'Pedidos listos para delivery',
    'Cuantos pedidos se entregaron hoy?',
    'Pedidos en ruta ahora mismo',
  ],
  'bot-ia': [
    'Crea un skill con información de mi negocio',
    'Que conocimientos tiene configurado mi bot?',
    'Configura los horarios de atención',
    'Consulta la configuración actual de IA',
  ],
  configuracion: [
    'Cambia el nombre de la app',
    'Actualiza los colores de la empresa',
    'Resumen de suscripción',
  ],
  general: [
    'Dame un resumen general de mi empresa',
    'Que deberia mejorar?',
    'Cuantos productos agotados tengo?',
    'Ventas de hoy hasta ahora',
  ],
};

const TIPO_LABELS = {
  crear_producto: 'Crear producto',
  editar_producto: 'Editar producto',
  eliminar_producto: 'Eliminar producto',
  ajustar_stock: 'Ajustar stock',
  crear_departamento: 'Crear departamento',
  editar_departamento: 'Editar departamento',
  eliminar_departamento: 'Eliminar departamento',
  crear_usuario: 'Crear usuario',
  editar_usuario: 'Editar usuario',
  editar_sede: 'Editar sede',
  editar_branding: 'Editar apariencia',
  crear_skill: 'Crear conocimiento',
  editar_skill: 'Editar conocimiento',
  eliminar_skill: 'Eliminar conocimiento',
  actualizar_config: 'Actualizar config IA',
};

function AsistenteIA({ pagina }) {
  const [abierto, setAbierto] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [acciones, setAcciones] = useState([]);
  const [aplicando, setAplicando] = useState(false);
  const [uso, setUso] = useState(null);
  const [toast, setToast] = useState(null);
  const [minimizado, setMinimizado] = useState(false);
  const [historialId, setHistorialId] = useState(null);
  const [historialList, setHistorialList] = useState([]);
  const [mostrarHistorial, setMostrarHistorial] = useState(false);
  const [datos, setDatos] = useState(null);
  const [modalDatos, setModalDatos] = useState(null);
  const [hablandoIdx, setHablandoIdx] = useState(null);
  const audioRef = useRef(null);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const historialRef = useRef(null);

  useEffect(() => {
    if (abierto && !uso) {
      API.get('/empresa-ia/uso')
        .then((r) => {
          if (r.data.data) setUso(r.data.data);
        })
        .catch(() => {});
    }
  }, [abierto, uso]);

  const cargarHistorial = useCallback(() => {
    API.get('/empresa-ia/historial')
      .then((r) => {
        setHistorialList(r.data.data || []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (abierto) cargarHistorial();
  }, [abierto, cargarHistorial]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (historialRef.current && !historialRef.current.contains(e.target)) {
        setMostrarHistorial(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (chatEndRef.current && abierto && !minimizado) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [msgs, abierto, minimizado]);

  useEffect(() => {
    if (abierto && !minimizado && inputRef.current) {
      inputRef.current.focus();
    }
  }, [abierto, minimizado]);

  const mostrarToast = useCallback((msg, tipo = 'success') => {
    setToast({ msg, tipo });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const detenerVoz = useCallback(() => {
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.src = '';
      } catch {}
      audioRef.current = null;
    }
    setHablandoIdx(null);
  }, []);

  const reproducirVoz = useCallback(
    async (texto, idx) => {
      const limpio = (texto || '')
        .replace(/[*_`#]/g, '')
        .replace(/\[DATOS\][\s\S]*/g, '')
        .replace(/\[ACCIONES\][\s\S]*/g, '')
        .trim();
      if (!limpio) return;
      if (hablandoIdx === idx) {
        detenerVoz();
        return;
      }
      detenerVoz();
      setHablandoIdx(idx);
      try {
        const res = await API.post(
          '/empresa-ia/voz',
          { texto: limpio },
          { responseType: 'blob', timeout: 30000 }
        );
        const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: 'audio/mpeg' });
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audioRef.current = audio;
        const limpiar = () => {
          URL.revokeObjectURL(url);
          if (audioRef.current === audio) {
            audioRef.current = null;
            setHablandoIdx(null);
          }
        };
        audio.onended = limpiar;
        audio.onerror = limpiar;
        await audio.play();
      } catch (e) {
        setHablandoIdx(null);
        mostrarToast('No se pudo reproducir el audio', 'error');
      }
    },
    [hablandoIdx, detenerVoz, mostrarToast]
  );

  const enviar = async (texto) => {
    const msg = (texto || input).trim();
    if (!msg || enviando) return;
    const nuevosMsgs = [...msgs, { rol: 'usuario', contenido: msg }];
    setMsgs(nuevosMsgs);
    setInput('');
    setEnviando(true);
    try {
      const res = await API.post('/empresa-ia/asistente', {
        mensajes: nuevosMsgs,
        seccion: pagina || 'general',
      });
      if (res.data?.status === 'error') {
        const errMsg = res.data?.message || 'No se pudo procesar la solicitud';
        setMsgs([...nuevosMsgs, { rol: 'asistente', contenido: errMsg }]);
        return;
      }
      const data = res.data.data;
      const msgsFinal = [...nuevosMsgs, { rol: 'asistente', contenido: data.respuesta }];
      setMsgs(msgsFinal);
      if (data.acciones && data.acciones.length > 0) setAcciones(data.acciones);
      setDatos(data.datos || null);
      if (data.uso)
        setUso((prev) =>
          prev
            ? { ...prev, mensajes_usados: data.uso.usados, mensajes_limite: data.uso.limite }
            : prev
        );
      autoGuardar(msgsFinal, historialId);
    } catch (e) {
      const errMsg =
        e.response?.data?.message ||
        e.response?.data?.detail ||
        'Error al comunicarse con el asistente';
      setMsgs([...nuevosMsgs, { rol: 'asistente', contenido: errMsg }]);
    } finally {
      setEnviando(false);
    }
  };

  const aplicar = async (accs) => {
    setAplicando(true);
    try {
      const res = await API.post('/empresa-ia/asistente/aplicar', { acciones: accs });
      const d = res.data?.data;
      if (!d) {
        mostrarToast(res.data?.message || 'Error al aplicar cambios', 'error');
        return;
      }
      const exitosos = d.exitosos || 0;
      const errores = d.resultados?.filter((r) => !r.ok) || [];
      if (errores.length > 0) {
        mostrarToast(
          `${exitosos} aplicados, ${errores.length} errores`,
          errores.length === d.total ? 'error' : 'success'
        );
      } else {
        mostrarToast(`${exitosos} cambio(s) aplicados`);
      }
      setAcciones([]);
      setMsgs((prev) => [
        ...prev,
        {
          rol: 'asistente',
          contenido: `Se aplicaron ${exitosos}/${d.total} cambios correctamente.${errores.length > 0 ? ' Errores: ' + errores.map((e) => e.error).join(', ') : ''}`,
        },
      ]);
    } catch (e) {
      mostrarToast('Error al aplicar cambios', 'error');
    } finally {
      setAplicando(false);
    }
  };

  const autoGuardar = useCallback(
    async (mensajes, id) => {
      if (mensajes.length < 1) return;
      try {
        const primerUsuario = mensajes.find((m) => m.rol === 'usuario');
        const titulo = primerUsuario ? primerUsuario.contenido.substring(0, 80) : 'Conversación';
        const res = await API.post('/empresa-ia/historial', { id, titulo, mensajes });
        if (res.data.data?.id) {
          if (!id) setHistorialId(res.data.data.id);
          cargarHistorial();
        }
      } catch (e) {
        /* silencioso */
      }
    },
    [cargarHistorial]
  );

  const nuevoChat = useCallback(() => {
    detenerVoz();
    setMsgs([]);
    setAcciones([]);
    setDatos(null);
    setModalDatos(null);
    setHistorialId(null);
    setInput('');
    setMostrarHistorial(false);
  }, [detenerVoz]);

  const cargarConversacion = useCallback(
    async (id) => {
      try {
        const res = await API.get(`/empresa-ia/historial/${id}`);
        const data = res.data.data;
        setMsgs(data.mensajes || []);
        setHistorialId(id);
        setAcciones([]);
        setDatos(null);
        setModalDatos(null);
        setMostrarHistorial(false);
      } catch (e) {
        mostrarToast('Error al cargar conversación', 'error');
      }
    },
    [mostrarToast]
  );

  const eliminarConversacion = useCallback(
    async (id, e) => {
      e.stopPropagation();
      if (!window.confirm('Eliminar esta conversación?')) return;
      try {
        await API.delete(`/empresa-ia/historial/${id}`);
        if (historialId === id) nuevoChat();
        cargarHistorial();
        mostrarToast('Conversación eliminada');
      } catch (e) {
        mostrarToast('Error al eliminar', 'error');
      }
    },
    [historialId, cargarHistorial, mostrarToast, nuevoChat]
  );

  const limiteAlcanzado =
    uso?.mensajes_limite != null && uso?.mensajes_usados >= uso?.mensajes_limite;
  const sugs = SUGERENCIAS[pagina] || SUGERENCIAS.general;

  const togglePanel = () => {
    if (abierto && !minimizado) {
      setMinimizado(true);
    } else if (abierto && minimizado) {
      setAbierto(false);
      setMinimizado(false);
    } else {
      setAbierto(true);
      setMinimizado(false);
    }
  };

  const accionResumen = (a) => {
    const d = a.datos || {};
    switch (a.tipo) {
      case 'crear_producto':
        return `${d.codigo || '?'}: ${d.descripcion || '?'} — $${d.precio1 || d.precio || 0}`;
      case 'editar_producto':
        return `${d.codigo}: ${d.campo} = ${d.valor}`;
      case 'eliminar_producto':
        return `Desactivar ${d.codigo}`;
      case 'ajustar_stock':
        return `${d.codigo} en ${d.sede}: ${d.tipo} ${d.cantidad}`;
      case 'crear_departamento':
        return `${d.codigo}: ${d.nombre}`;
      case 'crear_usuario':
        return `${d.nombre} (${d.rol || 'asesor'})`;
      case 'editar_usuario':
        return `${d.cedula}: ${d.campo} = ${d.valor}`;
      case 'editar_sede':
        return `${d.codigo}: ${d.campo} = ${d.valor}`;
      case 'editar_branding':
        return `${d.campo} = ${d.valor}`;
      case 'crear_skill':
        return a.titulo || 'Nuevo skill';
      case 'editar_skill':
        return `Skill #${a.skill_id}`;
      case 'eliminar_skill':
        return `Skill #${a.skill_id}`;
      case 'actualizar_config':
        return `${a.campo || d.campo}`;
      default:
        return a.titulo || a.tipo;
    }
  };

  return (
    <>
      <style>{`
        @keyframes asistente-spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @media (max-width:768px) {
          .asistente-panel { width:100% !important; right:0 !important; left:0 !important; top:auto !important; bottom:0 !important; height:80vh !important; border-radius:16px 16px 0 0 !important; }
        }
      `}</style>

      {toast && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            color: COL.white,
            padding: '12px 20px',
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: 600,
            zIndex: 10001,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            background: toast.tipo === 'error' ? COL.danger : COL.acc,
          }}
        >
          {toast.msg}
        </div>
      )}

      <button
        className="asistente-fab"
        onClick={() => {
          if (!abierto) {
            setAbierto(true);
            setMinimizado(false);
          } else togglePanel();
        }}
        title="Asistente IA"
        style={S.fab}
      >
        {abierto ? <FiMessageSquare size={18} /> : <FiCpu size={18} />}
        {uso?.mensajes_limite != null && !abierto && (
          <span style={S.fabCounter}>
            {uso.mensajes_usados}/{uso.mensajes_limite}
          </span>
        )}
      </button>

      {abierto && (
        <div
          className="asistente-panel"
          style={{ ...S.panel, height: minimizado ? '52px' : '560px' }}
        >
          <div style={S.panelHeader} onClick={() => setMinimizado(!minimizado)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FiCpu size={18} color={COL.white} />
              <span style={{ fontWeight: 600, color: COL.white, fontSize: '14px' }}>
                Asistente IA
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setMostrarHistorial(!mostrarHistorial);
                }}
                style={{ ...S.headerBtn, position: 'relative', marginLeft: '4px' }}
                title="Historial"
              >
                <FiClock size={14} />
                {historialList.length > 0 && (
                  <span
                    style={{
                      position: 'absolute',
                      top: '-2px',
                      right: '-2px',
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: COL.acc,
                    }}
                  />
                )}
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {uso?.mensajes_limite != null && (
                <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)' }}>
                  {uso.mensajes_usados}/{uso.mensajes_limite}
                </span>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setMinimizado(!minimizado);
                }}
                style={S.headerBtn}
              >
                <FiChevronDown
                  size={16}
                  style={{
                    transform: minimizado ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.2s',
                  }}
                />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setAbierto(false);
                  setMinimizado(false);
                }}
                style={S.headerBtn}
              >
                <FiX size={16} />
              </button>
            </div>
          </div>

          {mostrarHistorial && (
            <div
              ref={historialRef}
              style={S.historialDropdown}
              onClick={(e) => e.stopPropagation()}
            >
              <button onClick={nuevoChat} style={S.historialNuevo}>
                <FiPlus size={14} /> Nuevo chat
              </button>
              {historialList.length === 0 ? (
                <div
                  style={{
                    padding: '16px',
                    textAlign: 'center',
                    fontSize: '12px',
                    color: COL.muted,
                  }}
                >
                  No hay conversaciones guardadas
                </div>
              ) : (
                historialList.map((h) => (
                  <div
                    key={h.id}
                    onClick={() => cargarConversacion(h.id)}
                    style={{
                      ...S.historialItem,
                      background: h.id === historialId ? `${COL.pri2}10` : 'transparent',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: '13px',
                          color: COL.text,
                          fontWeight: 600,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {h.titulo}
                      </div>
                      <div style={{ fontSize: '10px', color: COL.muted, marginTop: '2px' }}>
                        {h.num_mensajes} mensajes &middot; {h.actualizado?.substring(0, 10)}
                      </div>
                    </div>
                    <button
                      onClick={(e) => eliminarConversacion(h.id, e)}
                      style={S.historialDelete}
                      title="Eliminar"
                    >
                      <FiTrash2 size={12} />
                    </button>
                  </div>
                ))
              )}
            </div>
          )}

          {!minimizado && (
            <>
              <div style={S.body}>
                {msgs.length === 0 && (
                  <div style={S.empty}>
                    <div
                      style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${COL.pri2}20, ${COL.pri}20)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <FiCpu size={24} color={COL.pri2} />
                    </div>
                    <div style={{ fontSize: '15px', fontWeight: 700, color: COL.pri }}>
                      Asistente empresarial
                    </div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: COL.muted,
                        textAlign: 'center',
                        lineHeight: '1.5',
                      }}
                    >
                      Puedo informarte, analizar y hacer cambios en tu empresa. Todo con tu
                      confirmación.
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '6px',
                        width: '100%',
                        marginTop: '4px',
                      }}
                    >
                      {sugs.map((s, i) => (
                        <button key={i} onClick={() => enviar(s)} style={S.sug}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {msgs.map((m, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      justifyContent: m.rol === 'usuario' ? 'flex-end' : 'flex-start',
                      marginBottom: '8px',
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: m.rol === 'usuario' ? 'flex-end' : 'flex-start', maxWidth: '100%' }}>
                      <div
                        style={{
                          ...S.bubble,
                          background: m.rol === 'usuario' ? COL.pri2 : COL.white,
                          color: m.rol === 'usuario' ? COL.white : COL.text,
                          borderRadius:
                            m.rol === 'usuario' ? '14px 14px 2px 14px' : '14px 14px 14px 2px',
                        }}
                        dangerouslySetInnerHTML={{ __html: fmtMd(m.contenido) }}
                      />
                      {m.rol === 'asistente' && (
                        <button
                          onClick={() => reproducirVoz(m.contenido, i)}
                          style={{ ...S.speakBtn, color: hablandoIdx === i ? COL.red : COL.muted }}
                          title={hablandoIdx === i ? 'Detener audio' : 'Escuchar respuesta'}
                        >
                          {hablandoIdx === i ? <FiSquare size={12} /> : <FiVolume2 size={12} />}
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                {enviando && (
                  <div
                    style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '8px' }}
                  >
                    <div
                      style={{
                        ...S.bubble,
                        background: COL.white,
                        color: COL.muted,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <FiCpu size={14} style={{ animation: 'asistente-spin 1s linear infinite' }} />
                      Consultando base de datos...
                    </div>
                  </div>
                )}

                {datos && <DatosEstructurados datos={datos} onExpand={() => setModalDatos(datos)} />}

                {acciones.length > 0 && (
                  <div style={S.accionesBox}>
                    <div
                      style={{
                        fontSize: '13px',
                        fontWeight: 700,
                        color: COL.pri,
                        marginBottom: '8px',
                      }}
                    >
                      Cambios propuestos ({acciones.length})
                    </div>
                    {acciones.map((a, i) => (
                      <div key={i} style={S.accionCard}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={S.accionBadge}>{TIPO_LABELS[a.tipo] || a.tipo}</span>
                        </div>
                        <div style={{ fontSize: '12px', color: COL.text, marginTop: '4px' }}>
                          {accionResumen(a)}
                        </div>
                        {a.contenido && (
                          <div
                            style={{
                              fontSize: '11px',
                              color: COL.muted,
                              marginTop: '2px',
                              maxHeight: '40px',
                              overflow: 'hidden',
                            }}
                          >
                            {a.contenido.substring(0, 120)}...
                          </div>
                        )}
                      </div>
                    ))}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                      <button
                        onClick={() => aplicar(acciones)}
                        disabled={aplicando}
                        style={S.btnApply}
                      >
                        <FiCheck size={14} /> {aplicando ? 'Aplicando...' : 'Confirmar todos'}
                      </button>
                      <button onClick={() => setAcciones([])} style={S.btnDiscard}>
                        <FiX size={14} /> Descartar
                      </button>
                    </div>
                  </div>
                )}

                <div ref={chatEndRef} />
              </div>

              {modalDatos && (
                <div
                  style={S.modalOverlay}
                  onClick={() => setModalDatos(null)}
                >
                  <div style={S.modal} onClick={(e) => e.stopPropagation()}>
                    <div style={S.modalHeader}>
                      <span style={{ fontWeight: 700, color: COL.pri, fontSize: '15px' }}>
                        {modalDatos.titulo || 'Detalle'}
                      </span>
                      <button onClick={() => setModalDatos(null)} style={S.headerBtn}>
                        <FiX size={18} />
                      </button>
                    </div>
                    <div style={S.modalBody}>
                      <DatosEstructurados datos={modalDatos} expandido />
                    </div>
                  </div>
                </div>
              )}

              <div style={S.inputBox}>
                {limiteAlcanzado ? (
                  <div
                    style={{
                      padding: '12px',
                      textAlign: 'center',
                      color: COL.danger,
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                    }}
                  >
                    <FiAlertCircle size={14} /> Limite de mensajes IA alcanzado
                  </div>
                ) : (
                  <>
                    <input
                      ref={inputRef}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && enviar()}
                      placeholder="Pregunta o solicita un cambio..."
                      style={S.inputField}
                      disabled={enviando}
                    />
                    <button
                      onClick={() => enviar()}
                      disabled={enviando || !input.trim()}
                      style={S.sendBtn}
                    >
                      <FiSend size={16} />
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}

// Render visual de los datos estructurados que devuelve la IA ([DATOS]).
function DatosEstructurados({ datos, expandido, onExpand }) {
  if (!datos) return null;
  const tipo = datos.tipo;
  const titulo = datos.titulo;

  if (tipo === 'metricas') {
    const metricas = datos.metricas || [];
    if (metricas.length === 0) return null;
    return (
      <div style={{ margin: '6px 0 8px' }}>
        {titulo && <div style={{ fontSize: '11px', fontWeight: 700, color: COL.muted, marginBottom: '6px' }}>{titulo}</div>}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {metricas.map((m, i) => (
            <div key={i} style={{ background: COL.white, border: `1px solid ${COL.border}`, borderRadius: '8px', padding: '8px 10px', minWidth: '90px' }}>
              <div style={{ fontSize: '10px', color: COL.muted }}>{m.label}</div>
              <div style={{ fontSize: '14px', fontWeight: 800, color: COL.pri }}>{m.valor}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (tipo === 'tabla') {
    const columnas = datos.columnas || [];
    const filas = datos.filas || [];
    if (columnas.length === 0 || filas.length === 0) return null;
    const visible = expandido ? filas : filas.slice(0, 4);
    return (
      <div style={{ margin: '6px 0 8px', background: COL.white, border: `1px solid ${COL.border}`, borderRadius: '10px', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', borderBottom: `1px solid ${COL.border}` }}>
          <span style={{ fontSize: '11px', fontWeight: 700, color: COL.muted }}>{titulo || 'Detalle'}</span>
          {!expandido && filas.length > 4 && (
            <button onClick={onExpand} style={{ ...S.speakBtn, color: COL.pri2 }}>
              <FiMaximize2 size={13} />
            </button>
          )}
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
          <thead>
            <tr style={{ background: COL.bg }}>
              {columnas.map((c, i) => (
                <th key={i} style={{ textAlign: 'left', padding: '6px 8px', color: COL.muted, fontSize: '9px', textTransform: 'uppercase', borderBottom: `1px solid ${COL.border}` }}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((f, ri) => (
              <tr key={ri} style={{ borderBottom: `1px solid ${COL.border}` }}>
                {f.map((cell, ci) => (
                  <td key={ci} style={{ padding: '6px 8px', color: COL.text, wordBreak: 'break-word' }}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {!expandido && filas.length > 4 && (
          <div style={{ padding: '6px 10px', fontSize: '10px', color: COL.muted }}>Mostrando {visible.length} de {filas.length} filas</div>
        )}
      </div>
    );
  }

  if (tipo === 'lista') {
    const items = datos.items || [];
    if (items.length === 0) return null;
    const visible = expandido ? items : items.slice(0, 4);
    return (
      <div style={{ margin: '6px 0 8px', background: COL.white, border: `1px solid ${COL.border}`, borderRadius: '10px', padding: '10px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
          <span style={{ fontSize: '11px', fontWeight: 700, color: COL.muted }}>{titulo || 'Detalle'}</span>
          {!expandido && items.length > 4 && (
            <button onClick={onExpand} style={{ ...S.speakBtn, color: COL.pri2 }}>
              <FiMaximize2 size={13} />
            </button>
          )}
        </div>
        <ul style={{ margin: 0, paddingLeft: '18px' }}>
          {visible.map((it, i) => (
            <li key={i} style={{ fontSize: '12px', color: COL.text, lineHeight: 1.6 }}>{it}</li>
          ))}
        </ul>
        {!expandido && items.length > 4 && (
          <div style={{ fontSize: '10px', color: COL.muted, marginTop: '4px' }}>Mostrando {visible.length} de {items.length}</div>
        )}
      </div>
    );
  }

  return null;
}

const S = {
  fab: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '6px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: COL.muted,
    fontSize: '18px',
    transition: 'color 0.2s',
    position: 'relative',
  },
  fabCounter: {
    position: 'absolute',
    top: '-2px',
    right: '-4px',
    fontSize: '9px',
    fontWeight: 700,
    background: COL.pri,
    color: COL.white,
    padding: '1px 4px',
    borderRadius: '8px',
    lineHeight: 1.2,
  },

  panel: {
    position: 'fixed',
    top: '64px',
    right: '16px',
    width: '400px',
    background: COL.white,
    borderRadius: '16px',
    boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 1000,
    overflow: 'hidden',
    transition: 'height 0.3s ease',
  },
  panelHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 16px',
    background: `linear-gradient(135deg, ${COL.pri2}, ${COL.pri})`,
    cursor: 'pointer',
    flexShrink: 0,
  },
  headerBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: COL.white,
    padding: '2px',
    display: 'flex',
    alignItems: 'center',
  },

  body: { flex: 1, overflow: 'auto', padding: '14px', background: 'var(--agx-bg2)' },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    padding: '16px 10px',
  },
  sug: {
    padding: '10px 14px',
    borderRadius: '10px',
    border: `1px solid ${COL.border}`,
    background: COL.white,
    color: COL.text,
    fontSize: '12px',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'all 0.15s',
  },
  bubble: {
    maxWidth: '85%',
    padding: '10px 14px',
    fontSize: '13px',
    lineHeight: '1.6',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  speakBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: COL.muted,
    padding: '3px 6px',
    borderRadius: '6px',
    marginTop: '2px',
    fontSize: '11px',
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15,23,42,0.55)',
    zIndex: 10002,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  modal: {
    background: COL.white,
    borderRadius: '14px',
    maxWidth: '760px',
    width: '100%',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 12px 40px rgba(16,24,40,0.25)',
    overflow: 'hidden',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 18px',
    borderBottom: `1px solid ${COL.border}`,
  },
  modalBody: { padding: '16px 18px', overflowY: 'auto' },

  accionesBox: {
    background: '#f0fdf4',
    borderRadius: '12px',
    padding: '14px',
    border: `1px solid ${COL.acc}30`,
    marginBottom: '8px',
  },
  accionCard: {
    background: COL.white,
    borderRadius: '8px',
    padding: '10px 12px',
    border: `1px solid ${COL.border}`,
    marginBottom: '6px',
  },
  accionBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '6px',
    background: '#eff6ff',
    color: COL.pri2,
    fontSize: '10px',
    fontWeight: 700,
  },
  btnApply: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '8px 14px',
    borderRadius: '8px',
    border: 'none',
    background: COL.acc,
    color: COL.white,
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    flex: 1,
    justifyContent: 'center',
  },
  btnDiscard: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '8px 14px',
    borderRadius: '8px',
    border: `1px solid ${COL.border}`,
    background: COL.white,
    color: COL.muted,
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
  },

  inputBox: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 12px',
    borderTop: `1px solid ${COL.border}`,
    gap: '8px',
    flexShrink: 0,
  },
  inputField: {
    flex: 1,
    padding: '10px 14px',
    borderRadius: '20px',
    border: `1px solid ${COL.border}`,
    fontSize: '13px',
    outline: 'none',
  },
  sendBtn: {
    width: '38px',
    height: '38px',
    borderRadius: '50%',
    border: 'none',
    background: COL.pri2,
    color: COL.white,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
  },

  historialDropdown: {
    position: 'absolute',
    top: '52px',
    left: '0',
    right: '0',
    background: COL.white,
    borderBottom: `1px solid ${COL.border}`,
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    maxHeight: '260px',
    overflowY: 'auto',
    zIndex: 10,
    borderBottomLeftRadius: '8px',
    borderBottomRightRadius: '8px',
  },
  historialNuevo: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    width: '100%',
    padding: '10px 14px',
    border: 'none',
    borderBottom: `1px solid ${COL.border}`,
    background: COL.white,
    color: COL.pri2,
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    textAlign: 'left',
  },
  historialItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 14px',
    borderBottom: `1px solid ${COL.border}`,
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  historialDelete: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: COL.muted,
    padding: '4px',
    borderRadius: '4px',
    flexShrink: 0,
  },
};

export default AsistenteIA;
